/**
 * 工作台布局：顶部 bar + 左侧导航菜单侧边栏 + 右侧主区域。
 *
 * 侧边栏菜单：社区广场 / 文生图 / 模型生成 / 我的作品；
 * 侧边栏左下角账号（ChatGPT 式：头像 + 名字 + ⋯ 触发器），
 * 弹出菜单：账号管理 / 我的订阅 / 帮助中心 / 登出。
 * 已登录：显示完整工作台；未登录：显示登录引导。
 */

import { type ReactNode, useState, useEffect } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
} from "@clerk/clerk-react";
import { reportFingerprint } from "../lib/fingerprint";
import { LocalizedLink, useLang } from "../lib/lang";
import { USER_BUTTON_POPOVER_CARD } from "../lib/clerkAppearance";
import { Menu, Mail, CreditCard, Home } from "./icons";
import LocaleSwitcher from "./LocaleSwitcher";
import Logo from "./Brand";

export default function WorkbenchLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 路由态弹窗（我的订阅）打开中：主路由树按背景 location 匹配（见 App.tsx），
  // 侧边栏 active 判定同样用背景路径，弹窗期间高亮保持原页面不变。
  const modalBackground = (location.state as { background?: Location } | null)?.background;

  // 进入工作台时上报一次设备指纹（防多账号薅羊毛，fire-and-forget）
  useEffect(() => {
    void reportFingerprint("signup");
  }, []);

  return (
    <div className="workbench">
      <header className="wb-header">
        <div className="wb-header-left">
          <button
            className="wb-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={t("nav.menuToggle")}
          >
            <Menu size={18} />
          </button>
          <Logo variant="workbench" />
        </div>
        <div className="wb-header-right">
          <LocaleSwitcher />
          {/* 首页入口：语言切换右侧，跳转落地页 */}
          <LocalizedLink
            to="/"
            className="wb-home-btn"
            aria-label={t("nav.home")}
            title={t("nav.home")}
          >
            <Home size={16} />
          </LocalizedLink>
          {/* 账号已移至左侧栏底部；未登录时仍提供 Sign in 入口 */}
          <SignedOut>
            <SignInButton mode="modal">
              <button className="nav-btn nav-signin">{t("common.signIn")}</button>
            </SignInButton>
          </SignedOut>
        </div>
      </header>

      <div className="wb-body">
        <SignedIn>
          <aside
            className={`wb-sidebar ${sidebarOpen ? "open" : ""}`}
            onClick={() => setSidebarOpen(false)}
          >
            <SidebarContent currentPath={modalBackground?.pathname ?? location.pathname} />
          </aside>
          {sidebarOpen && (
            <div
              className="wb-sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </SignedIn>

        <main className="wb-main">
          <SignedIn>{children}</SignedIn>
          <SignedOut>
            <div className="auth-prompt">
              <h2>{t("workbench.authPrompt.title")}</h2>
              <p>{t("workbench.authPrompt.body")}</p>
              <SignInButton mode="modal">
                <button className="generate-btn">
                  {t("workbench.authPrompt.cta")}
                </button>
              </SignInButton>
            </div>
          </SignedOut>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ currentPath }: { currentPath: string }) {
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  /** 打开「我的订阅」路由态弹窗：SPA 导航免整页重载；state.background 记录
   * 背景页（已在弹窗中时沿用原背景，避免背景指向弹窗路由自身）。 */
  function openBilling() {
    const state = location.state as { background?: Location } | null;
    navigate(`/${lang}/workbench/billing`, {
      state: { background: state?.background ?? location },
    });
  }
  // active 判定需匹配带语言前缀的路径（如 /zh/workbench/generate）
  // 用 endsWith 避免依赖具体语言前缀。
  const navItems = [
    {
      to: "/workbench/plaza",
      label: t("nav.plaza"),
      active:
        currentPath.endsWith("/workbench/plaza") ||
        /\/workbench\/plaza\//.test(currentPath),
    },
    {
      to: "/workbench/image-gen",
      label: t("nav.imageGen"),
      active: currentPath.endsWith("/workbench/image-gen"),
    },
    {
      to: "/workbench/generate",
      label: t("nav.generate"),
      // 生成页 + 生成结果/等待页 都算「模型生成」在用
      active:
        currentPath.endsWith("/workbench/generate") ||
        /\/workbench\/generations\//.test(currentPath),
    },
    {
      to: "/workbench/history",
      label: t("nav.history"),
      active: currentPath.endsWith("/workbench/history"),
    },
  ];

  return (
    <div className="wb-sidebar-content">
      {/* 顶部导航菜单 */}
      <nav className="wb-nav">
        {navItems.map((item) => (
          <LocalizedLink
            key={item.to}
            to={item.to}
            className={`wb-nav-item ${item.active ? "active" : ""}`}
          >
            <span className="wb-nav-label">{item.label}</span>
          </LocalizedLink>
        ))}
      </nav>

      {/* 左下角账号（ChatGPT 式）：头像 + 名字 + ⋯ 的整行触发器，弹出菜单含
          账号管理 / 我的订阅 / 帮助中心 / 登出。通过 appearance 注入自定义类名
          （Clerk 公共 API，避免目标内部 cl-* 类名随版本变化）。
          菜单项一律用 UserButton.Action + SPA navigate：Link 的 href 是原生 <a>，
          点击会整页重载（我的订阅弹窗因此又慢又闪）；我的订阅走路由态弹窗
          （state.background 记录背景页，主路由树保持背景页挂载，见 App.tsx）；
          帮助中心指向 FAQ（Clerk 菜单 Link 对 mailto: 有缺陷，不用 mailto 直链）。 */}
      <div className="wb-sidebar-footer">
        <UserButton
          afterSignOutUrl="/"
          showName
          appearance={{
            elements: {
              userButtonTrigger: "wb-ub-trigger",
              userButtonTriggerBox: "wb-ub-box",
              userButtonUserName: "wb-ub-name",
              // 弹出菜单加宽，与首页顶栏账号弹层一致
              userButtonPopoverCard: USER_BUTTON_POPOVER_CARD,
            },
          }}
        >
          <UserButton.MenuItems>
            <UserButton.Action
              label={t("nav.billing")}
              labelIcon={<CreditCard size={16} />}
              onClick={openBilling}
            />
            <UserButton.Action
              label={t("nav.helpCenter")}
              labelIcon={<Mail size={16} />}
              onClick={() => navigate(`/${lang}/faq`)}
            />
          </UserButton.MenuItems>
        </UserButton>
      </div>
    </div>
  );
}
